import type { Quaternion } from 'three';
import { Matrix4, Vector3 } from 'three';
import { BALANCE } from '../config/balance';
import { clamp, degToRad, quaternionFromBasis, rightFromForwardUp } from '../utils/math';
import type { Robot } from '../player/Robot';
import type { CollisionWorld } from '../world/CollisionWorld';
import type { CameraTransform } from './CameraTransform';

const scratchForward = new Vector3();
const scratchRight = new Vector3();
const scratchDesired = new Vector3();
const scratchAnchor = new Vector3();
const scratchLookAt = new Vector3();
const scratchOffset = new Vector3();
const scratchCross = new Vector3();
const scratchZ = new Vector3();
const scratchX = new Vector3();
const scratchY = new Vector3();
const scratchRolled = new Vector3();
const scratchMatrix = new Matrix4();

/**
 * The navigation camera: a spring arm behind the robot, aligned to whatever
 * surface the robot is standing on.
 */
export class ThirdPersonCamera {
  private readonly targetPosition = new Vector3();
  private initialised = false;

  constructor(private readonly collision: CollisionWorld) {}

  reset(): void {
    this.initialised = false;
  }

  /**
   * Compute the camera transform for the current frame.
   * `viewForward` is the direction the player is steering in, projected onto
   * the robot's surface. `up` is the body's up as the camera sees it, which
   * is damped while the body turns in mid air. `pitchOffset` orbits the arm
   * vertically around the robot (positive looks up). `aimForward` is where the
   * camera looks; when it is omitted the camera looks at the robot.
   * `viewOffset` is the head-tracking viewer offset in camera-local metres,
   * already scaled by the caller's parallax scale: it translates the camera,
   * so the window is a real parallax rather than a shifted frustum over a
   * camera that never moves.
   */
  update(
    dt: number,
    robot: Robot,
    viewForward: Vector3,
    up: Vector3,
    out: CameraTransform,
    pitchOffset = 0,
    aimForward?: Vector3,
    aimRoll = 0,
    viewOffset?: Vector3,
  ): void {
    const settings = BALANCE.camera.thirdPerson;

    scratchForward.copy(viewForward);
    scratchForward.addScaledVector(up, -scratchForward.dot(up));
    if (scratchForward.lengthSq() < 1e-6) scratchForward.copy(robot.forward);
    scratchForward.normalize();

    // The camera sits behind the robot along the view direction.
    scratchDesired
      .copy(robot.position)
      .addScaledVector(scratchForward, -settings.distance)
      .addScaledVector(up, settings.height);

    // Orbit the arm around the robot's right axis. At zero offset this is the
    // tuned rest pose; the limits keep the camera out of the floor and sky.
    const orbitPitch = clamp(pitchOffset, degToRad(settings.minPitch), degToRad(settings.maxPitch));
    if (Math.abs(orbitPitch) > 1e-5) {
      scratchRight.crossVectors(scratchForward, up).normalize();
      scratchOffset.copy(scratchDesired).sub(robot.position);
      scratchCross.crossVectors(scratchRight, scratchOffset);
      scratchOffset
        .multiplyScalar(Math.cos(orbitPitch))
        .addScaledVector(scratchCross, Math.sin(orbitPitch));
      scratchDesired.copy(robot.position).add(scratchOffset);
    }

    // Move the camera with the head-tracking viewer, along the very axes the
    // off-axis frustum will use. Doing it here rather than after the arm has
    // settled means the clearance, collision and zone tests all see the moved
    // camera, so a lean can still not push it through a wall. With the
    // frustum shifted by the same offset, points at the virtual screen stay
    // put and everything else slides by depth: a real parallax.
    if (viewOffset && (viewOffset.x !== 0 || viewOffset.y !== 0 || viewOffset.z !== 0)) {
      const forward = aimForward ?? scratchForward;
      rightFromForwardUp(forward, up, scratchX);
      if (scratchX.lengthSq() < 1e-8) scratchX.set(1, 0, 0);
      scratchY.crossVectors(scratchX, forward).normalize();
      scratchDesired
        .addScaledVector(scratchX, viewOffset.x)
        .addScaledVector(scratchY, viewOffset.y)
        .addScaledVector(forward, viewOffset.z);
    }

    // Do not let the camera sink through the surface the robot stands on.
    const clearance = robot.surface ? robot.surface.signedDistance(scratchDesired) : Infinity;
    if (clearance < 0.25) {
      scratchDesired.addScaledVector(up, 0.25 - clearance);
    }

    // Pull the camera in when something blocks the view.
    scratchAnchor.copy(robot.position).addScaledVector(up, 0.2);
    scratchRight.copy(scratchDesired).sub(scratchAnchor);
    const distance = scratchRight.length();
    if (distance > 1e-4) {
      scratchRight.divideScalar(distance);
      const hit = this.collision.raycast(scratchAnchor, scratchRight, distance);
      if (hit) {
        scratchDesired
          .copy(scratchAnchor)
          .addScaledVector(scratchRight, Math.max(0.4, hit.distance - settings.collisionRadius));
      }
    }

    // Keep the camera inside its zone. Rounding a corner swings the arm
    // through the walls, so the desired position is pushed back inside. Only
    // the containing zone is tested: testing every zone would shove the camera
    // through a shared wall into the neighbouring room.
    this.collision.room.pushInside(scratchDesired, 0.15);

    if (!this.initialised) {
      this.targetPosition.copy(scratchDesired);
      this.initialised = true;
    } else {
      const smoothing = 1 - Math.exp(-settings.smoothing * dt);
      this.targetPosition.lerp(scratchDesired, smoothing);
    }

    out.position.copy(this.targetPosition);
    if (aimForward) {
      aimQuaternion(aimForward, up, aimRoll, out.quaternion);
    } else {
      scratchLookAt.copy(robot.position);
      lookAtQuaternion(out.position, scratchLookAt, up, out.quaternion);
    }
  }
}

/** Quaternion that looks along `direction` with the given up vector and roll. */
export function aimQuaternion(
  direction: Vector3,
  up: Vector3,
  roll: number,
  out: Quaternion,
): Quaternion {
  rightFromForwardUp(direction, up, scratchX);
  if (scratchX.lengthSq() < 1e-8) {
    // Looking straight along the up axis leaves no unique right vector.
    scratchX.set(1, 0, 0);
  }
  scratchY.crossVectors(scratchX, direction).normalize();
  if (roll !== 0) {
    const cos = Math.cos(roll);
    const sin = Math.sin(roll);
    scratchRolled.copy(scratchX).multiplyScalar(cos).addScaledVector(scratchY, sin);
    scratchY.multiplyScalar(cos).addScaledVector(scratchX, -sin).normalize();
    scratchX.copy(scratchRolled).normalize();
  }
  scratchZ.copy(direction).negate();
  return quaternionFromBasis(scratchX, scratchY, scratchZ, out);
}

/** Quaternion that looks from `from` towards `to` with the given up vector. */
export function lookAtQuaternion(
  from: Vector3,
  to: Vector3,
  up: Vector3,
  out: Quaternion,
): Quaternion {
  scratchZ.subVectors(from, to);
  if (scratchZ.lengthSq() < 1e-8) scratchZ.set(0, 0, 1);
  scratchZ.normalize();
  scratchX.crossVectors(up, scratchZ);
  if (scratchX.lengthSq() < 1e-8) scratchX.set(1, 0, 0);
  scratchX.normalize();
  scratchY.crossVectors(scratchZ, scratchX);
  scratchMatrix.makeBasis(scratchX, scratchY, scratchZ);
  return out.setFromRotationMatrix(scratchMatrix);
}
